import { useCallback, useState } from "react";

import {
  EntityType,
  Subscription,
} from "../../api/grpc/subscription/subscription";
import { useAuthContext } from "../../lib/context/Auth/AuthContext";
import { createSubscriptionCheckout } from "../Subscription/lib/createSubscriptionCheckout";
import { SubscriptionAddOn } from "./productStoreSubscriptionAddOn";

interface SubscriptionQuery {
  data?: Subscription;
  refetch: () => void;
}

/** Buying a subscription add-on, such as Total Care, through checkout. */
export const useSubscriptionAddOnCheckout = ({
  data,
  refetch,
}: SubscriptionQuery) => {
  const { user, tokenInterceptor } = useAuthContext();
  // The add-on whose "Order now" opened checkout.
  const [addOn, setAddOn] = useState<SubscriptionAddOn>();
  // Add-ons bought here, shown as included before the refetched subscription
  // has them.
  const [justAdded, setJustAdded] = useState<EntityType[]>([]);

  const createCheckout = useCallback(
    ({ priceId }: SubscriptionAddOn) =>
      createSubscriptionCheckout(
        user?.customerid || "",
        data?.id || "",
        tokenInterceptor,
        [{ itemPriceID: priceId, quantity: 1 }]
      ),
    [data?.id, tokenInterceptor, user?.customerid]
  );

  // After any purchase that added subscription items, e.g. Total Care in a
  // hardware cart.
  const markAdded = useCallback(
    (entityTypes: EntityType[]) => {
      if (entityTypes.length) {
        setJustAdded((previous) => [...previous, ...entityTypes]);
        refetch();
      }
    },
    [refetch]
  );

  const complete = useCallback(
    ({ entityType }: SubscriptionAddOn) => markAdded([entityType]),
    [markAdded]
  );

  return {
    addOn,
    /** What the subscription has, including add-ons just bought here. */
    ownedAddOns: [
      ...(data?.addons.map((addon) => addon.entityType) ?? []),
      ...justAdded,
    ],
    open: setAddOn,
    close: () => setAddOn(undefined),
    createCheckout,
    complete,
    markAdded,
  };
};
